const LA_AND_PROPOSER_SAME_FIELD_KEYS = [
  "GOPAslifetobeAssuredandProposer",
  "islifetobeAssuredandProposer",
]

const HEADING_TEMPLATES = {
  personalDetails: {
    single: "Personal Details of Life Assured",
    shared: "Personal Details of Life Assured / Proposer",
  },
  residenceOccupation: {
    single: "Life Assured Residence and Occupation Details",
    shared: "Life Assured / Proposer Residence and Occupation Details",
  },
  communicationAddress: {
    single: "Communication Address of Life Assured",
    shared: "Communication Address of Life Assured / Proposer",
  },
  occupationDetails: {
    single: "Occupation Details of Life Assured",
    shared: "Occupation Details of Life Assured / Proposer",
  },
  insuranceHistory: {
    single: "Insurance History of Life Assured",
    shared: "Insurance History of Life Assured / Proposer",
  },
  healthLifestyle: {
    single: "Health & Lifestyle Details of Life Assured",
    shared: "Health & Lifestyle Details of Life Assured / Proposer",
  },
  nriQuestionnaires: {
    single: "NRI Questionnaires of Life Assured",
    shared: "NRI Questionnaires of Life Assured / Proposer",
  },
  occupationalQuestionnaire: {
    single: "Occupational Questionaire of Life Assured",
    shared: "Occupational Questionaire of Life Assured / Proposer",
  },
}

const SECTION_ROLE_RULES = [
  {
    role: "personalDetails",
    matches: normalizedText =>
      includesAll(normalizedText, ["personal", "detail"]),
  },
  {
    role: "residenceOccupation",
    matches: normalizedText =>
      includesAll(normalizedText, ["occupation", "detail"]) &&
      includesAny(normalizedText, ["residence", "resident"]),
  },
  {
    role: "communicationAddress",
    matches: normalizedText =>
      includesAll(normalizedText, ["communication", "address"]),
  },
  {
    role: "occupationDetails",
    matches: normalizedText =>
      includesAll(normalizedText, ["occupation", "detail"]) &&
      !includesAny(normalizedText, ["residence", "resident", "questionnaire"]),
  },
  {
    role: "insuranceHistory",
    matches: normalizedText =>
      includesAll(normalizedText, ["insurance", "history"]),
  },
  {
    role: "healthLifestyle",
    matches: normalizedText =>
      includesAll(normalizedText, ["health", "lifestyle"]),
  },
  {
    role: "nriQuestionnaires",
    matches: normalizedText =>
      includesAll(normalizedText, ["nri", "question"]),
  },
  {
    role: "occupationalQuestionnaire",
    matches: normalizedText =>
      includesAll(normalizedText, ["occupational", "question"]),
  },
]

const EXCLUDED_SECTION_TERMS = [
  "spouse",
  "joint life",
  "jointlife",
  "appointee",
  "nominee",
]

const includesAll = (text, values) =>
  values.every(value => text.includes(value))

const includesAny = (text, values) =>
  values.some(value => text.includes(value))

const normalizeText = value =>
  (value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/&/g, " and ")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .toLowerCase()
    .trim()

const getYesNoValue = value => {
  if (value === "Y" || value === "N") {
    return value
  }
}

const getSectionDataLaAndProposerSame = sectionData => {
  for (const fieldKey of LA_AND_PROPOSER_SAME_FIELD_KEYS) {
    const value = getYesNoValue(sectionData?.[fieldKey])

    if (value) {
      return value
    }
  }
}

const getConditionStateLaAndProposerSame = conditionState => {
  for (const [key, rawValue] of Object.entries(conditionState || {})) {
    const matchesField = LA_AND_PROPOSER_SAME_FIELD_KEYS.some(fieldKey =>
      key.endsWith(`.${fieldKey}`)
    )
    const value = getYesNoValue(rawValue)

    if (matchesField && value) {
      return value
    }
  }
}

const getJourneyDataLaAndProposerSame = journeyState => {
  for (const page of journeyState?.journeyData || []) {
    for (const section of page?.pageData || []) {
      const value = getSectionDataLaAndProposerSame(section?.sectionData)

      if (value) {
        return value
      }
    }
  }
}

const inferLaAndProposerSameFromSections = sections => {
  if (Array.isArray(sections)) {
    const hasProposerSection = sections.some(section => {
      const sectionId = section?.sectionId || ""
      const sectionName = section?.name || ""

      return /proposer/i.test(sectionId) || /proposer/i.test(sectionName)
    })

    return hasProposerSection ? "N" : "Y"
  }
}

const getHeadingRole = section => {
  const normalizedText = normalizeText(
    `${section?.sectionId || ""} ${section?.name || ""}`
  )

  if (!normalizedText) {
    return null
  }

  if (includesAny(normalizedText, EXCLUDED_SECTION_TERMS)) {
    return null
  }

  const matchedRule = SECTION_ROLE_RULES.find(rule =>
    rule.matches(normalizedText)
  )

  return matchedRule?.role || null
}

const getHeadingTextForRole = (role, isLaAndProposerSame) => {
  const template = HEADING_TEMPLATES[role]

  if (!template) {
    return ""
  }

  if (isLaAndProposerSame === "Y") {
    return template.shared
  }

  if (isLaAndProposerSame === "N") {
    return template.single
  }

  return ""
}

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const replaceHeadingContent = (content, role, isLaAndProposerSame) => {
  if (!content || !role) {
    return content || ""
  }

  const nextHeading = getHeadingTextForRole(role, isLaAndProposerSame)
  const template = HEADING_TEMPLATES[role]

  if (!nextHeading || !template) {
    return content
  }

  const patterns = [template.single, template.shared].map(
    value => new RegExp(escapeRegExp(value), "gi")
  )

  return patterns.reduce(
    (updatedContent, pattern) => updatedContent.replace(pattern, nextHeading),
    content
  )
}

const getLaAndProposerSame = ({
  conditionState,
  sectionState,
  journeyState,
  sections,
  allowSectionInference = false,
}) => {
  const conditionValue = getConditionStateLaAndProposerSame(conditionState)

  if (conditionValue) {
    return conditionValue
  }

  const sectionValue = getSectionDataLaAndProposerSame(sectionState?.sectionData)

  if (sectionValue) {
    return sectionValue
  }

  const journeyValue = getJourneyDataLaAndProposerSame(journeyState)

  if (journeyValue) {
    return journeyValue
  }

  if (allowSectionInference) {
    return inferLaAndProposerSameFromSections(sections)
  }
}

export const getSectionDisplayName = ({
  section,
  conditionState,
  sectionState,
  journeyState,
  sections,
}) => {
  if (!section) {
    return ""
  }

  const role = getHeadingRole(section)
  if (!role) {
    return section.name || ""
  }

  const isLaAndProposerSame = getLaAndProposerSame({
    conditionState,
    sectionState,
    journeyState,
    sections,
    allowSectionInference: true,
  })

  return getHeadingTextForRole(role, isLaAndProposerSame) || section.name || ""
}

export const getDynamicTextContent = ({
  sectionId,
  content,
  conditionState,
  sectionState,
  journeyState,
  sections,
}) => {
  const role = getHeadingRole({ sectionId, name: content })
  if (!role) {
    return content || ""
  }

  const isLaAndProposerSame = getLaAndProposerSame({
    conditionState,
    sectionState,
    journeyState,
    sections,
    allowSectionInference: true,
  })

  return replaceHeadingContent(content, role, isLaAndProposerSame)
}
